import {type ComponentType, type LazyExoticComponent} from 'react';
import {matchPath} from 'react-router';

export type NestedRouteEntry = {
  paths: string[];
  datadogViewName: string;
};

export type NestedRouteConfig = {
  entries: NestedRouteEntry[];
  exact?: boolean;
  /** Pre-computed flat array of all paths across entries. */
  allPaths: string[];
};

/**
 * A single nested route that binds a {@link NestedRouteConfig} to a lazy-loaded Component.
 * Used for iterable route arrays where each entry maps to a `<Route>` in a `<Switch>`.
 *
 * The parent component (e.g. HomePageWithData) iterates these to render routes
 * and resolves the RUM view name via {@link resolveNestedRouteName}.
 */
export type NestedRoute = {
  Component: LazyExoticComponent<ComponentType<any>>;
  nestedRouteConfig: NestedRouteConfig;
  fallbackDatadogViewName: string;
};

/**
 * A named collection of {@link NestedRouteConfig}s, where each key corresponds
 * to a `<Route>` in the parent component's `<Switch>`.
 *
 * The parent component renders each config's `allPaths` as a `<Route path>` and
 * uses {@link resolveNestedRouteName} inside the `render` prop for granular
 * RUM view name tracking.
 *
 * Routes that use nested route definitions must set `skipTracking: true` in
 * their routesData entry to prevent double-tracking at the AppRoutes level.
 */
export type NestedRouteConfigDefinitions = Record<string, NestedRouteConfig>;

export function defineNestedRouteConfig(
  config: Omit<NestedRouteConfig, 'allPaths'>
): NestedRouteConfig {
  return {
    ...config,
    allPaths: config.entries.flatMap(e => e.paths),
  };
}

/**
 * Resolves the RUM view name for a pathname within a specific nested route config.
 * Iterates through the config's entries (order matters — most specific first)
 * and returns the datadogViewName of the first match.
 */
export function resolveNestedRouteName({
  pathname,
  nestedRouteConfig,
  fallbackDatadogViewName,
}: {
  pathname: string;
  nestedRouteConfig: NestedRouteConfig;
  fallbackDatadogViewName: string;
}) {
  for (const entry of nestedRouteConfig.entries) {
    const match = matchPath(pathname, {
      path: entry.paths,
      exact: nestedRouteConfig.exact ?? false,
    });
    if (match) {
      return entry.datadogViewName;
    }
  }
  return fallbackDatadogViewName;
}
